import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {SORT} from '../incase-domain/common/sort.enum';
import {createListParams, ListParams, SetListParamsOptions, UseListParamsReturn} from './listParams';

export const PAGE_PARAM = 'page';
const SIZE_PARAM = 'size';
export const SEARCH_PARAM = 'search';
const SORT_PARAM = 'sort';
const ORDER_PARAM = 'order';

const SIZE_MULTIPLIER_COUNT = 5;

function getAllowedSizes(defaultSize: number): number[] {
    return Array.from({length: SIZE_MULTIPLIER_COUNT}, (_, index) => defaultSize * (index + 1));
}

interface SortConfig {
    allowedFields: string[];
}

interface FilterFieldConfig {
    allowedValues?: readonly string[];
    multiple?: boolean;
}

interface ListParamsUrlConfig {
    sort?: SortConfig;
    filterFields?: Record<string, FilterFieldConfig>;
}

const clampPage = (page: number): number => Math.max(1, Math.floor(page) || 1);

const clampSize = (size: number, defaultSize: number): number => {
    const allowedSizes = getAllowedSizes(defaultSize);
    if (allowedSizes.includes(size)) {
        return size;
    }
    return defaultSize;
};

const getActiveSortEntry = (sortBy: Record<string, SORT>, sortConfig: SortConfig | undefined): [string, SORT] | undefined =>
    Object.entries(sortBy).find(
        ([field, direction]) => sortConfig?.allowedFields.includes(field) && (direction === SORT.ASC || direction === SORT.DESC)
    );

const normalizeSortBy = (sortBy: Record<string, SORT>, sortConfig: SortConfig | undefined): Record<string, SORT> => {
    if (!sortConfig) {
        return sortBy;
    }

    const activeEntry = getActiveSortEntry(sortBy, sortConfig);

    return activeEntry ? {[activeEntry[0]]: activeEntry[1]} : {};
};

const parseSortFromUrl = (searchParams: URLSearchParams, sortConfig: SortConfig | undefined): Record<string, SORT> => {
    if (!sortConfig) {
        return {};
    }

    const field = searchParams.get(SORT_PARAM);
    const order = searchParams.get(ORDER_PARAM);

    if (!field || !sortConfig.allowedFields.includes(field)) {
        return {};
    }

    if (order === SORT.ASC || order === SORT.DESC) {
        return {[field]: order};
    }

    return {};
};

const serializeSortToUrl = (
    sortBy: Record<string, SORT>,
    sortConfig: SortConfig | undefined,
    params: URLSearchParams,
    initialSortBy: Record<string, SORT> = {}
): void => {
    if (!sortConfig) {
        return;
    }

    const activeEntry = getActiveSortEntry(sortBy, sortConfig);
    const initialActiveEntry = getActiveSortEntry(initialSortBy, sortConfig);

    if (activeEntry && (!initialActiveEntry || activeEntry[0] !== initialActiveEntry[0] || activeEntry[1] !== initialActiveEntry[1])) {
        const [field, direction] = activeEntry;
        params.set(SORT_PARAM, field);
        params.set(ORDER_PARAM, direction);
    } else {
        params.delete(SORT_PARAM);
        params.delete(ORDER_PARAM);
    }
};

const DEFAULT_FILTER_VALUE = '';

const isAllowedFilterValue = (value: string, fieldConfig: FilterFieldConfig): boolean => {
    const {allowedValues} = fieldConfig;

    if (value === DEFAULT_FILTER_VALUE || !allowedValues) {
        return true;
    }

    if (!fieldConfig.multiple) {
        return allowedValues.includes(value);
    }

    const values = value.split(',');
    return values.length > 0 && values.every((singleValue) => singleValue !== '' && allowedValues.includes(singleValue));
};

const normalizeFilterValue = (value: string | null, fieldConfig: FilterFieldConfig): string => {
    if (value === null) {
        return DEFAULT_FILTER_VALUE;
    }

    if (isAllowedFilterValue(value, fieldConfig)) {
        return value;
    }

    return DEFAULT_FILTER_VALUE;
};

const getInitialFilterValue = (initialFilters: Record<string, string>, key: string, fieldConfig: FilterFieldConfig): string => {
    const initialValue = initialFilters[key];
    return initialValue === undefined ? DEFAULT_FILTER_VALUE : normalizeFilterValue(initialValue, fieldConfig);
};

const parseFiltersFromUrl = (
    searchParams: URLSearchParams,
    filterFields: Record<string, FilterFieldConfig> | undefined,
    initialFilters: Record<string, string> = {}
): Record<string, string> => {
    if (!filterFields) {
        return {};
    }

    return Object.entries(filterFields).reduce((acc, [key, fieldConfig]) => {
        const raw = searchParams.get(key);

        if (raw === null) {
            const initialValue = initialFilters[key];
            acc[key] =
                initialValue !== undefined ? normalizeFilterValue(initialValue, fieldConfig) : normalizeFilterValue(null, fieldConfig);
        } else {
            acc[key] = normalizeFilterValue(raw, fieldConfig);
        }

        return acc;
    }, {} as Record<string, string>);
};

const serializeFiltersToUrl = (
    filters: Record<string, string>,
    filterFields: Record<string, FilterFieldConfig> | undefined,
    params: URLSearchParams,
    initialFilters: Record<string, string> = {}
): void => {
    if (!filterFields) {
        return;
    }

    Object.entries(filterFields).forEach(([key, fieldConfig]) => {
        const defaultValue = getInitialFilterValue(initialFilters, key, fieldConfig);
        const value = filters[key] ?? defaultValue;
        if (value === defaultValue || !isAllowedFilterValue(value, fieldConfig)) {
            params.delete(key);
        } else {
            params.set(key, value);
        }
    });
};

export const useListSearchParams = (initial: Partial<ListParams> = {}, urlConfig: ListParamsUrlConfig = {}): UseListParamsReturn => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialParams = useMemo(() => createListParams(initial), [initial]);

    const listParams: ListParams = useMemo(() => {
        const page = clampPage(Number(searchParams.get(PAGE_PARAM)) || initialParams.page);
        const size = clampSize(Number(searchParams.get(SIZE_PARAM)) || initialParams.size, initialParams.size);
        const search = searchParams.get(SEARCH_PARAM) ?? initialParams.search;

        const sortFromUrl = parseSortFromUrl(searchParams, urlConfig.sort);
        const sortBy = Object.keys(sortFromUrl).length > 0 ? sortFromUrl : normalizeSortBy(initialParams.sortBy, urlConfig.sort);

        const filters = parseFiltersFromUrl(searchParams, urlConfig.filterFields, initialParams.filters);

        return {
            page,
            size,
            search,
            sortBy,
            filters,
        };
    }, [searchParams, urlConfig.sort, urlConfig.filterFields, initialParams]);

    const applyParams = useCallback(
        (next: ListParams, options: SetListParamsOptions = {}) => {
            const params = new URLSearchParams(searchParams);

            const page = clampPage(next.page);
            const size = clampSize(next.size, initialParams.size);
            const {search} = next;

            if (page !== initialParams.page) {
                params.set(PAGE_PARAM, String(page));
            } else {
                params.delete(PAGE_PARAM);
            }
            if (size !== initialParams.size) {
                params.set(SIZE_PARAM, String(size));
            } else {
                params.delete(SIZE_PARAM);
            }
            if (search !== initialParams.search) {
                params.set(SEARCH_PARAM, search);
            } else {
                params.delete(SEARCH_PARAM);
            }

            serializeSortToUrl(next.sortBy, urlConfig.sort, params, initialParams.sortBy);
            serializeFiltersToUrl(next.filters, urlConfig.filterFields, params, initialParams.filters);

            setSearchParams(params, {replace: options.replace ?? true});
        },
        [urlConfig.sort, urlConfig.filterFields, initialParams, searchParams, setSearchParams]
    );

    const setListParams = useCallback(
        (partial: Partial<ListParams>, options: SetListParamsOptions = {}) => {
            const resetPage = options.resetPage ?? false;
            const next: ListParams = {...listParams, ...partial};

            if (resetPage) {
                next.page = initialParams.page;
            }

            applyParams(next, options);
        },
        [applyParams, initialParams.page, listParams]
    );

    return {
        listParams,
        setListParams,
    };
};
